import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import axios from "axios";
import {
    Loader2,
    ChevronLeft,
    Building2,
    Briefcase,
    FileText,
    Link as LinkIcon,
    MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { useTeamAuth } from "@/lib/teamAuth";
import { findGrowthService } from "@/lib/growthServices";
import TeamShell from "@/components/TeamShell";
import ChatPanel from "@/components/ChatPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamClientDetail() {
    const { id } = useParams();
    const { member, token, loading } = useTeamAuth();
    const [busy, setBusy] = useState(true);
    const [data, setData] = useState(null);
    const [activeProject, setActiveProject] = useState(null);
    const [projectData, setProjectData] = useState(null);
    const [projBusy, setProjBusy] = useState(false);

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    useEffect(() => {
        if (!token || !id) return;
        setBusy(true);
        axios
            .get(`${API}/team/clients/${id}`, { headers })
            .then((r) => {
                setData(r.data);
                if (r.data.projects?.length && !activeProject) {
                    setActiveProject(r.data.projects[0].id);
                }
            })
            .catch((err) => {
                const msg = err?.response?.data?.detail || "Client not found or not assigned";
                toast.error(typeof msg === "string" ? msg : "Load failed");
            })
            .finally(() => setBusy(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, id]);

    useEffect(() => {
        if (!activeProject) {
            setProjectData(null);
            return;
        }
        setProjBusy(true);
        axios
            .get(`${API}/team/clients/${id}/projects/${activeProject}`, { headers })
            .then((r) => setProjectData(r.data))
            .catch(() => toast.error("Could not load project"))
            .finally(() => setProjBusy(false));
    }, [activeProject, id, headers]);

    if (loading)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (!member) return <Navigate to="/team/login" replace />;

    const cl = data?.client;

    return (
        <TeamShell>
            <Link
                to="/team"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-bone-500 hover:text-ember transition-colors mb-8"
                data-testid="team-back-to-clients"
            >
                <ChevronLeft className="w-4 h-4" />
                Back to clients
            </Link>

            {busy || !cl ? (
                <div className="flex items-center gap-3 text-bone-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading client…
                </div>
            ) : (
                <>
                    <header className="mb-10 border border-white/10 bg-ink-800/40 p-8">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-ember mb-2">
                                    Client
                                </div>
                                <h1 className="font-display text-bone text-5xl tracking-tightest leading-[1.05]" data-testid="team-client-name">
                                    {cl.name}
                                </h1>
                                {cl.company && (
                                    <p className="text-bone-500 text-base mt-2 inline-flex items-center gap-2">
                                        <Building2 className="w-4 h-4" />
                                        {cl.company}
                                    </p>
                                )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                                {cl.plan && (
                                    <span className="px-3 py-1.5 border border-white/15 uppercase tracking-[0.2em] text-[10px] text-bone">
                                        {cl.plan}
                                    </span>
                                )}
                                <span className="text-[11px] text-bone-500">
                                    Member since{" "}
                                    {cl.created_at
                                        ? new Date(cl.created_at).toLocaleDateString()
                                        : "—"}
                                </span>
                            </div>
                        </div>
                        {cl.plan_summary && (
                            <p className="mt-6 text-sm text-bone-300 leading-relaxed border-t border-white/5 pt-5 whitespace-pre-wrap">
                                {cl.plan_summary}
                            </p>
                        )}
                        <div className="mt-6 inline-flex items-center gap-2 px-3 py-2 border border-white/10 text-[11px] text-bone-500">
                            <MessageSquare className="w-3.5 h-3.5 text-ember" />
                            Personal contact details are private. Coordinate via chat below.
                        </div>
                    </header>

                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                        {/* Projects column */}
                        <div className="lg:col-span-3 space-y-6">
                            <div className="border border-white/10 bg-ink-800/40">
                                <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
                                    <Briefcase className="w-4 h-4 text-ember" />
                                    <h2 className="font-display text-bone text-base">Projects</h2>
                                </div>
                                {data.projects?.length === 0 ? (
                                    <p className="px-5 py-8 text-sm text-bone-500">
                                        No projects yet.
                                    </p>
                                ) : (
                                    <div className="divide-y divide-white/5">
                                        {data.projects.map((p) => (
                                            <button
                                                key={p.id}
                                                type="button"
                                                onClick={() => setActiveProject(p.id)}
                                                data-testid={`team-project-${p.id}`}
                                                className={`w-full text-left px-5 py-4 transition-colors ${
                                                    activeProject === p.id
                                                        ? "bg-ember/5"
                                                        : "hover:bg-white/[0.02]"
                                                }`}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div>
                                                        <h3 className="font-display text-bone text-lg tracking-tight">
                                                            {p.title}
                                                        </h3>
                                                        <div className="flex items-center gap-2 mt-1 text-[10px] uppercase tracking-[0.2em] text-bone-500">
                                                            <span className="px-1.5 py-0.5 border border-white/10">
                                                                {p.status}
                                                            </span>
                                                            <span>
                                                                {p.milestone_done}/{p.milestone_total} milestones
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <span className="font-display text-2xl text-ember">
                                                        {p.progress}%
                                                    </span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {activeProject && (
                                <ProjectDetailCard
                                    busy={projBusy}
                                    data={projectData}
                                />
                            )}
                        </div>

                        {/* Chat column */}
                        <div className="lg:col-span-2">
                            <ChatPanel
                                clientId={cl.id}
                                token={token}
                                actorType="team"
                                actorName={member.name}
                                sla={data?.sla}
                                title={`Chat with ${cl.name}`}
                                subtitle="Replies are visible to the client and admin team."
                            />
                        </div>
                    </div>
                </>
            )}
        </TeamShell>
    );
}

function ProjectDetailCard({ busy, data }) {
    if (busy)
        return (
            <div className="border border-white/10 bg-ink-800/40 p-5 flex items-center gap-3 text-bone-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading project…
            </div>
        );
    if (!data) return null;

    const { project, milestones, resources, scripts, comments } = data;

    return (
        <div className="border border-white/10 bg-ink-800/40">
            <div className="px-5 py-4 border-b border-white/10">
                <h3 className="font-display text-bone text-lg tracking-tight">
                    {project.title}
                </h3>
                {project.description && (
                    <p className="text-sm text-bone-500 mt-1">{project.description}</p>
                )}
                {project.services?.length > 0 && (
                    <div
                        data-testid="team-project-services"
                        className="mt-3 flex flex-wrap gap-1.5"
                    >
                        {project.services.map((raw) => {
                            const svc = findGrowthService(raw);
                            return (
                                <span
                                    key={svc.id}
                                    data-testid={`team-project-service-${svc.id}`}
                                    title={svc.description || ""}
                                    className="inline-flex items-center px-2.5 py-1 border border-ember/30 bg-ember/5 text-ember text-[10px] uppercase tracking-[0.18em]"
                                >
                                    {svc.label}
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>

            <Section title="Milestones">
                {milestones.length === 0 ? (
                    <p className="text-sm text-bone-500">No milestones.</p>
                ) : (
                    <ol className="space-y-2">
                        {milestones.map((m) => (
                            <li
                                key={m.id}
                                className="flex items-start justify-between gap-3 border border-white/5 px-3 py-2"
                            >
                                <div>
                                    <p className="text-sm text-bone">{m.title}</p>
                                    {m.due_at && (
                                        <p className="text-[10px] text-bone-500 mt-0.5 uppercase tracking-[0.2em]">
                                            Due {new Date(m.due_at).toLocaleDateString()}
                                        </p>
                                    )}
                                </div>
                                <span className="text-[10px] uppercase tracking-[0.2em] text-bone-500">
                                    {m.status}
                                </span>
                            </li>
                        ))}
                    </ol>
                )}
            </Section>

            <Section title="Resources" icon={LinkIcon}>
                {resources.length === 0 ? (
                    <p className="text-sm text-bone-500">No resources shared yet.</p>
                ) : (
                    <ul className="space-y-2">
                        {resources.map((r) => (
                            <li key={r.id}>
                                <a
                                    href={r.url}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="text-sm text-ember link-underline"
                                >
                                    {r.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                )}
            </Section>

            <Section title="Scripts" icon={FileText}>
                {scripts.length === 0 ? (
                    <p className="text-sm text-bone-500">No scripts uploaded.</p>
                ) : (
                    <ul className="space-y-2">
                        {scripts.map((s) => (
                            <li
                                key={s.id}
                                className="flex items-center justify-between gap-3 border border-white/5 px-3 py-2"
                            >
                                <div>
                                    <p className="text-sm text-bone">{s.title}</p>
                                    <p className="text-[10px] text-bone-500 mt-0.5 uppercase tracking-[0.2em]">
                                        v{s.version || "1"} ·{" "}
                                        {new Date(s.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Section>

            <Section title="Comment thread">
                {comments.length === 0 ? (
                    <p className="text-sm text-bone-500">No comments on this project.</p>
                ) : (
                    <div className="space-y-3 max-h-72 overflow-y-auto">
                        {comments.map((c) => (
                            <div key={c.id} className="border border-white/5 px-3 py-2">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-bone-500 mb-1">
                                    {c.author_name || c.author_type} ·{" "}
                                    {new Date(c.created_at).toLocaleString()}
                                </div>
                                <p className="text-sm text-bone whitespace-pre-wrap">{c.body}</p>
                            </div>
                        ))}
                    </div>
                )}
            </Section>
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <div className="border-t border-white/5 px-5 py-4">
            <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.25em] text-bone-500">
                {Icon && <Icon className="w-3.5 h-3.5" />}
                {title}
            </div>
            {children}
        </div>
    );
}
